import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter} from "@/components/ui/dialog.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Form, FormField, FormItem, FormLabel, FormControl, FormMessage} from "@/components/ui/form.tsx";
import {useForm} from "react-hook-form";
import {usePage} from "@inertiajs/react";
import {DoctorEditFormPayload, DoctorUpdateSchema} from "@/lib/types/forms.ts";
import {zodResolver} from "@hookform/resolvers/zod";
import {Doctor} from "@/lib/types/models.ts";
import {useEffect} from "react";

interface EditDoctorDialogProps {
    isOpen: boolean,
    onOpenChange: (open: boolean) => void,
    onUpdate: (data: DoctorEditFormPayload) => Promise<void>,
    doctor: Doctor
}

export default function EditDoctorDialog({
                                             isOpen,
                                             onOpenChange,
                                             onUpdate,
                                             doctor
                                         }: EditDoctorDialogProps) {
    const {translations} = usePage().props as unknown as { translations: any };
    const t = translations.edit_dialog;

    const form = useForm<DoctorEditFormPayload>({
        resolver: zodResolver(DoctorUpdateSchema),
        defaultValues: {
            name: doctor.name ?? "",
            username: doctor.username ?? "",
            phone_number: doctor.phone_number ?? "",
            color: doctor.color ?? "#0000c8",
        },
    });

    useEffect(() => {
        form.reset({
            name: doctor.name ?? "",
            username: doctor.username ?? "",
            phone_number: doctor.phone_number ?? "",
            color: doctor.color ?? "#0000c8",
        });
    }, [doctor]);

    const handleSubmit = async (data: DoctorEditFormPayload) => {
        await onUpdate(data);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.name}</FormLabel>
                                    <FormControl>
                                        <Input {...field}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="username"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.username}</FormLabel>
                                    <FormControl>
                                        <Input {...field}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="phone_number"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.phone_number}</FormLabel>
                                    <FormControl>
                                        <Input {...field}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField control={form.control} name="color" render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t.labels.color}</FormLabel>
                                <FormControl>
                                    <input type="color" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )} />
                        <DialogFooter className="flex justify-end gap-2">
                            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                                {t.buttons.cancel}
                            </Button>
                            <Button type="submit">
                                {t.buttons.update}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
